import {
  DataRoomAccessPermission,
  DataRoomAccessStatus,
  DataRoomDetailView,
  DataRoomRecipientKind,
  DataRoomShareEffect,
  DataRoomStatus,
} from 'shared';

import { StakeholderType } from '../../types/stakeholder.types';

export type CreateDataRoomDto = {
  description?: string;
  hideStakeholderNames?: boolean;
  includeCapTable?: boolean;
  name: string;
  showInFolderStats?: boolean;
};

export type PatchDataRoomDto = {
  description?: null | string;
  hideStakeholderNames?: boolean;
  includeCapTable?: boolean;
  name?: string;
  showInFolderStats?: boolean;
  status?: DataRoomStatus;
};

export type DataRoomDetail = DataRoomDetailView;

export enum DataRoomAccessAction {
  REACTIVATE = 'reactivate',
  RESEND = 'resend',
  REVOKE = 'revoke',
}

export type DataRoomAccessRow = {
  // Null unless this address has a CapQuest account that signed in through Google; the row falls
  // back to an initial on a tinted circle.
  avatarUrl: null | string;
  email: string;
  grantedAt: string;
  id: string;
  lastActiveAt: null | string;
  // Null for an external recipient who has not completed signup; the email stands in for the name.
  name: null | string;
  organisation: string;
  permission: DataRoomAccessPermission;
  recipientKind: DataRoomRecipientKind;
  revokedAt: null | string;
  stakeholderId: null | string;
  status: DataRoomAccessStatus;
  // Null for an external recipient — stakeholder type is an internal concept.
  type: null | StakeholderType;
};

export type DataRoomAccessPage = {
  count: number;
  // False while the room is Inactive: the whole list is read-only.
  isEditable: boolean;
  records: DataRoomAccessRow[];
  roomStatus: DataRoomStatus;
  totalPages: number;
};

export type AccessQuery = {
  recipientKind?: DataRoomRecipientKind;
  search?: string;
  skip?: number;
  status?: DataRoomAccessStatus;
  take?: number;
  type?: StakeholderType;
};

export type ShareCandidate = {
  email: string;
  // Already on the access list: the row stays visible but cannot be selected again.
  hasAccess: boolean;
  id: string;
  isActive: boolean;
  name: string;
  organisation: string;
  shareEffect: DataRoomShareEffect;
  type: StakeholderType;
};

export type ShareCandidatePage = {
  count: number;
  records: ShareCandidate[];
  totalPages: number;
};

export type ShareCandidatesQuery = {
  organisation?: string;
  search?: string;
  skip?: number;
  take?: number;
  type?: StakeholderType;
};

export type ShareRecipient = {
  email: string;
  permission: DataRoomAccessPermission;
};

export type PermissionChange = {
  accessId: string;
  permission: DataRoomAccessPermission;
};

export type DataRoomInvite = {
  companyName: string;
  dataRoomName: string;
  /** The address the invitation was issued to; signing up under any other one is refused. */
  email: string;
  /** Null once the invitation has been accepted: the link stays usable and has nothing left to wait on. */
  expiresAt: null | string;
  hasAccount: boolean;
};

export type AcceptedDataRoomInvite = {
  /** The company the room is in — the one the session has to be switched to before landing. */
  companyId: string;
  dataRoomId: string;
  dataRoomName: string;
};
